<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteName } from '@/app/route-names'
import StepIndicator from '@/components/common/StepIndicator.vue'
import { Button } from '@/components/ui/button'
import { useOrganizationOnboardingStep } from '@/features/organizations/composables/useOrganizationOnboardingStep'
import { can } from '@/lib/permissions/can'
import { Permission } from '@/lib/permissions/keys'

const { t } = useI18n()
const { data, step } = useOrganizationOnboardingStep()

const steps = computed(() => data.value?.steps ?? [])
const stepLabels = computed(() =>
  steps.value.map((item) => t(`organizations.onboarding.steps.${item}`)),
)
const current = computed(() => (step.value ? steps.value.indexOf(step.value) : 0))
const action = computed(() => {
  if (step.value === 'create_website_request' && can(Permission.WebsiteCreate)) {
    return { to: '/websites/requests/new', label: t('organizations.onboarding.createRequest') }
  }

  const websiteId = data.value?.website_id

  if (step.value === 'request_in_progress' && websiteId && can(Permission.WebsiteList)) {
    return {
      to: { name: RouteName.WebsiteRequest, params: { id: websiteId } },
      label: t('organizations.onboarding.viewRequest'),
    }
  }

  return null
})
</script>

<template>
  <section v-if="step" class="bg-card mb-6 flex flex-col gap-4 rounded-2xl border p-6">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0 flex-1 space-y-1">
        <h2 class="font-semibold">{{ t(`organizations.onboarding.titles.${step}`) }}</h2>
        <p class="text-muted-foreground text-sm">
          {{ t(`organizations.onboarding.descriptions.${step}`) }}
        </p>
      </div>
      <Button v-if="action" as-child size="sm">
        <RouterLink :to="action.to">{{ action.label }}</RouterLink>
      </Button>
    </div>
    <StepIndicator :steps="stepLabels" :current="current" />
  </section>
</template>
